/**
 * Servis workspace tab strip — the one source of truth for the Subtabs row
 * shared by PlanovaniPage / VyhledaniPage (servis module) and UskladneniList /
 * HistorieList / LokaceList (uskladneni module).
 *
 * Uskladnění is its own managed module since access overhaul Stage 8, so the
 * strip is grant-filtered: 'planovani' + 'vyhledani' need servis:read,
 * 'uskladneni' + 'historie' + 'lokace' need uskladneni:read, and 'sprava'
 * (the change history of both) needs admin on either. While membership
 * is still unresolved every tab stays visible (the pre-Stage-8 behavior) —
 * the API remains the hard boundary.
 */

import { useMemo } from 'react';
import { useModuleAccess, type Modul, type NeedUroven } from '@/lib/permissions';

export type ServisTab =
  | 'planovani'
  | 'vyhledani'
  | 'uskladneni'
  | 'historie'
  | 'lokace'
  | 'sprava';

export interface ServisTabOption {
  value: ServisTab;
  label: string;
}

const SERVIS_TAB_OPTIONS: ServisTabOption[] = [
  { value: 'planovani', label: 'Plánování' },
  { value: 'vyhledani', label: 'Vyhledání servisu' },
  { value: 'uskladneni', label: 'Uskladnění pneumatik' },
  { value: 'historie', label: 'Historie výměn' },
  { value: 'lokace', label: 'Lokace' },
  { value: 'sprava', label: 'Správa' },
];

/** Route behind each tab — every page's strip navigates the same way. */
export const SERVIS_TAB_ROUTES = {
  planovani: '/servis/planovani',
  vyhledani: '/servis/vyhledani',
  uskladneni: '/servis/uskladneni',
  historie: '/servis/historie',
  lokace: '/servis/lokace',
  sprava: '/servis/sprava',
} as const satisfies Record<ServisTab, string>;

/** Which module grant (and level) carries each tab — Správa: admin on either. */
const TAB_GRANT: Record<ServisTab, { moduly: Modul[]; uroven: NeedUroven }> = {
  planovani: { moduly: ['servis'], uroven: 'read' },
  vyhledani: { moduly: ['servis'], uroven: 'read' },
  uskladneni: { moduly: ['uskladneni'], uroven: 'read' },
  historie: { moduly: ['uskladneni'], uroven: 'read' },
  lokace: { moduly: ['uskladneni'], uroven: 'read' },
  sprava: { moduly: ['servis', 'uskladneni'], uroven: 'admin' },
};

/** The servis-workspace tabs the current member may open (see file header). */
export function useServisTabs(): ServisTabOption[] {
  const { can, resolved } = useModuleAccess();
  return useMemo(
    () =>
      resolved
        ? SERVIS_TAB_OPTIONS.filter((o) => {
            const { moduly, uroven } = TAB_GRANT[o.value];
            return moduly.some((m) => can(m, uroven));
          })
        : SERVIS_TAB_OPTIONS.filter((o) => o.value !== 'sprava'),
    [can, resolved],
  );
}
